import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ShoppingCart, TrendingDown, CheckCircle2, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { StatCard } from './abandoned/StatCard';
import { AbandonedCartItem } from './abandoned/AbandonedCartItem';

export const AdminAbandonedCarts = () => {
  const [carts, setCarts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get('/admin/cart-analytics'),
        api.get(`/admin/abandoned-carts?hours=${hours}`),
      ]);
      setStats(s.data);
      setCarts(c.data);
    } catch (e) {
      toast.error('Failed to load carts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);

  const markContacted = async (id) => {
    try {
      await api.put(`/admin/abandoned-carts/${id}/contacted`);
      toast.success('Marked as contacted');
      setCarts(prev => prev.map(c => c.id === id ? { ...c, contacted: true } : c));
    } catch {
      toast.error('Failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="abandoned-carts-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-800">Abandoned Carts</h1>
          <p className="text-gray-500 text-sm mt-1">Visitors who added items but didn't checkout</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Inactive for</label>
          <select
            data-testid="hours-filter"
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value))}
            className="border rounded-md px-2 py-1.5 text-sm"
          >
            <option value={1}>1 hour+</option>
            <option value={6}>6 hours+</option>
            <option value={24}>24 hours+</option>
            <option value={72}>3 days+</option>
            <option value={168}>1 week+</option>
          </select>
          <Button onClick={load} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Activity} label="Active Carts" value={stats.active} color="bg-blue-500" testid="stat-active" />
          <StatCard icon={ShoppingCart} label="Abandoned" value={stats.abandoned} color="bg-orange-500" testid="stat-abandoned" />
          <StatCard icon={CheckCircle2} label="Converted" value={stats.converted} color="bg-green-500" testid="stat-converted" />
          <StatCard icon={TrendingDown} label="Lost Revenue" value={`₹${Math.round(stats.abandoned_value).toLocaleString('en-IN')}`} color="bg-red-500" testid="stat-lost-revenue" />
        </div>
      )}

      {stats && stats.total_sessions > 0 && (
        <Card className="mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-orange-600">{stats.conversion_rate}%</p>
            </div>
            <div className="text-xs text-gray-500 text-right">
              {stats.converted} converted / {stats.total_sessions} total sessions<br />
              {stats.cleared} cleared without checking out
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {loading ? 'Loading...' : `${carts.length} abandoned cart${carts.length !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!loading && carts.length === 0 && (
            <div className="text-center py-16 text-gray-500" data-testid="no-carts">
              <ShoppingCart size={40} className="mx-auto mb-3 opacity-40" />
              <p>No abandoned carts yet.</p>
            </div>
          )}
          {carts.map((cart) => (
            <AbandonedCartItem key={cart.id} cart={cart} onMarkContacted={markContacted} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAbandonedCarts;
